import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Crown, Zap } from "lucide-react";
import api from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const fmt = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const iconFor = (type) => (type === "vip_expiry" ? Crown : Zap);

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const unread = items.filter((n) => !n.read).length;

  const load = async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data || []);
    } catch (_) {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const onOpenChange = async (open) => {
    if (open && unread > 0) {
      try {
        await api.post("/notifications/read-all");
        setItems((list) => list.map((n) => ({ ...n, read: true })));
      } catch (_) {}
    }
  };

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="nav-notification-bell"
          className="relative w-10 h-10 rounded-full flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/5 active:scale-95 transition-all"
          aria-label="Benachrichtigungen"
        >
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span
              data-testid="notification-unread-badge"
              className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-fuchsia-500 text-white text-[10px] font-bold flex items-center justify-center border border-[#0b0c12]"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="glass border-white/10 text-slate-200 w-80 p-0 overflow-hidden"
        data-testid="notification-panel"
      >
        <div className="px-4 py-3 border-b border-white/10">
          <p className="font-display font-bold text-white text-sm">Benachrichtigungen</p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-500" data-testid="notification-empty">
              Keine Benachrichtigungen
            </p>
          ) : (
            items.map((n) => {
              const Icon = iconFor(n.type);
              return (
                <button
                  key={n.id}
                  data-testid="notification-item"
                  onClick={() => n.type === "vip_expiry" && navigate("/vip")}
                  className={`w-full text-left flex gap-3 px-4 py-3 border-b border-white/5 last:border-0 transition-all ${
                    n.type === "vip_expiry" ? "hover:bg-white/5 cursor-pointer" : "cursor-default"
                  } ${!n.read ? "bg-fuchsia-500/[0.06]" : ""}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${n.type === "vip_expiry" ? "bg-amber-500/15 text-amber-300" : "bg-cyan-500/15 text-cyan-300"}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-white leading-snug">{n.message}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{fmt(n.created_at)}</p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
